/**
 * Client-side country detection for GDPR scoping. Per the product decision (2026-07-18) this is a
 * best-effort CLIENT-side IP lookup; failure/blocked/unknown ⇒ treated as OUT of scope (no banner). It
 * runs ASYNC and off the render path — it must never block the UI.
 *
 * Primary: Cloudflare's free, no-key trace endpoint (`loc=XX`). Fallback: ipapi.co/json (`country_code`).
 */

/** EU-27 + EEA (Iceland, Liechtenstein, Norway) — the in-scope set (jurisdiction decision: EU/EEA only). */
export const EEA_COUNTRIES: ReadonlySet<string> = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
]);

export function isInScopeCountry(country: string | null | undefined): boolean {
  return !!country && EEA_COUNTRIES.has(country.toUpperCase());
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: ctrl.signal, credentials: 'omit', cache: 'no-store' });
  } catch {
    return null; // network error / blocked / aborted
  } finally {
    clearTimeout(t);
  }
}

async function fromCloudflare(timeoutMs: number): Promise<string | null> {
  const res = await fetchWithTimeout('https://www.cloudflare.com/cdn-cgi/trace', timeoutMs);
  if (!res?.ok) return null;
  const text = await res.text();
  const m = text.match(/^loc=([A-Za-z]{2})$/m);
  return m ? m[1]!.toUpperCase() : null;
}

async function fromIpapi(timeoutMs: number): Promise<string | null> {
  const res = await fetchWithTimeout('https://ipapi.co/json/', timeoutMs);
  if (!res?.ok) return null;
  try {
    const json = (await res.json()) as { country_code?: string; country?: string };
    const c = json.country_code ?? json.country;
    return typeof c === 'string' && c.length === 2 ? c.toUpperCase() : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the caller's 2-letter country code, or null when it can't be determined (⇒ out of scope).
 * Never throws — always resolves. Total time is bounded by roughly `2 × timeoutMs` worst case.
 */
export async function detectCountry(timeoutMs = 3000): Promise<string | null> {
  return (await fromCloudflare(timeoutMs)) ?? (await fromIpapi(timeoutMs));
}
